import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Box,
  Button,
  Container,
  CircularProgress,
  TableContainer,
  TableSortLabel,
  TextField,
  InputAdornment,
  Stack,
  Typography,
  Autocomplete,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import DeleteIcon from "@mui/icons-material/Delete";
import { textFieldStyles } from "../styles/textFieldStyles";
import FileAutocomplete from "../components/FileAutocomplete";

import { fetchFiles } from "../redux/actions/fileActions";
import BreadcrumbNav from "../components/BreadcrumbNav";
import { actionButtonStyles } from "../styles/actionButton";
import { windowsTableStyles } from "../styles/windowsTableStyles";
import { listDisbursements, deleteDisbursement } from "../redux/actions/disbursementActions";
import { DISBURSEMENT_DELETE_RESET } from "../redux/constants/disbursementConstants";

// Static status filter translations - defined outside component to prevent re-renders
const STATUS_FILTER_TRANSLATIONS = {
  'billed': {
    en: 'Billed',
    nl: 'Gefactureerd'
  },
  'not_billed': {
    en: 'Not billed',
    nl: 'Niet gefactureerd'
  },
  'not_billable': {
    en: 'Not billable',
    nl: 'Niet factureerbaar'
  }
};

const breadcrumbs = [{ label: "Home", href: "/" }, { label: "Disbursements" }];

const DisbursementsPage = () => {
  const { t, i18n } = useTranslation(['pages', 'common', 'buttons', 'forms']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tableStyles = windowsTableStyles();

  // Safe status filter translation function - uses static translations
  const translateStatusFilter = (status) => {
    const currentLang = i18n.language || 'en';
    
    // Direct mapping for better reliability
    if (currentLang === 'nl') {
      switch (status) {
        case 'billed': return 'Gefactureerd';
        case 'not_billed': return 'Niet gefactureerd';
        case 'not_billable': return 'Niet factureerbaar';
        default: return status;
      }
    } else {
      // English translations
      switch (status) {
        case 'billed': return 'Billed';
        case 'not_billed': return 'Not billed';
        case 'not_billable': return 'Not billable';
        default: return status;
      }
    }
  };

  const { loading, disbursements } = useSelector(
    (state) => state.disbursementList
  ); // Redux state for disbursements

  const { files } = useSelector((state) => state.fileList);
  const { user } = useSelector((state) => state.userLoaded); //  Redux state for user files

  // Redux state for delete operations
  const {
    loading: deleteLoading,
    success: deleteSuccess,
    error: deleteError
  } = useSelector((state) => state.disbursementDelete);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [caseFilter, setCaseFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [delayedCheck, setDelayedCheck] = useState(false);
  const [shouldFetchFresh, setShouldFetchFresh] = useState(false);
  const hasInitiallyFetched = useRef(false);

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [disbursementToDelete, setDisbursementToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // State for notification snackbar
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDelayedCheck(true);
    }, 1000); // Delay in milliseconds

    return () => clearTimeout(timer); // Cleanup the timer
  }, []);

  useEffect(() => {
    // Fetch files if not loaded
    if (!files || files.length === 0) {
      dispatch(fetchFiles());
    }
  }, [dispatch, files]);

  // Single coordinated initial fetch
  useEffect(() => {
    if (!hasInitiallyFetched.current && (files?.length > 0)) {
      const filters = {};
      
      if (caseFilter) {
        filters.fileCase = caseFilter._id;
      }
      if (statusFilter) {
        if (statusFilter === translateStatusFilter('billed')) {
          filters.isBilled = true;
        } else if (statusFilter === translateStatusFilter('not_billed')) {
          filters.isBilled = false;
        }
      }
      
      dispatch(listDisbursements(filters));
      hasInitiallyFetched.current = true;
    }
  }, [files, caseFilter, statusFilter, dispatch, translateStatusFilter]);

  // Fetch fresh data from database when filters change (after initial load)
  useEffect(() => {
    if (shouldFetchFresh && hasInitiallyFetched.current) {
      const filters = {};
      
      if (caseFilter) {
        filters.fileCase = caseFilter._id;
      }
      if (statusFilter) {
        // Map translated status back to boolean
        if (statusFilter === translateStatusFilter('billed')) {
          filters.isBilled = true;
        } else if (statusFilter === translateStatusFilter('not_billed')) {
          filters.isBilled = false;
        }
        // Note: 'not_billable' doesn't have a direct filter in backend
      }
      
      dispatch(listDisbursements(filters));
      setShouldFetchFresh(false);
    }
  }, [shouldFetchFresh, caseFilter, statusFilter, dispatch, translateStatusFilter]);

  // Reset fetch flag when returning to page
  useEffect(() => {
    return () => {
      hasInitiallyFetched.current = false;
    };
  }, []);

  // Handle delete success or error
  useEffect(() => {
    if (deleteSuccess && !isDeleting) {
      setNotification({
        open: true,
        message: t('pages:disbursements.deleted_successfully'),
        severity: 'success'
      });
      dispatch(listDisbursements()); // Refresh the list
    } else if (deleteError && !isDeleting) {
      setNotification({
        open: true,
        message: deleteError,
        severity: 'error'
      });
    }
  }, [deleteSuccess, deleteError, isDeleting, dispatch, t]);

  // Handle opening delete confirmation dialog
  const handleDeleteClick = (disbursement) => {
    setDisbursementToDelete(disbursement);
    setDeleteDialogOpen(true);
  };

  // Handle confirming delete
  const handleConfirmDelete = async () => {
    if (disbursementToDelete) {
      setIsDeleting(true);
      await dispatch(deleteDisbursement(disbursementToDelete._id));
      setTimeout(() => {
        setIsDeleting(false);
      }, 1000);
    }
    setDeleteDialogOpen(false);
    setDisbursementToDelete(null);
  };

  // Handle canceling delete
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDisbursementToDelete(null);
  };

  // Handle notification close
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
    // Reset the delete state
    dispatch({ type: DISBURSEMENT_DELETE_RESET });
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const truncateText = (text, maxLength) => {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + "...";
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Filter files based on user role and permissions with proper null checks
  const userFiles = files?.filter((file) => {
    // Add comprehensive null checks for safety
    if (!user || !file || !Array.isArray(file.officeMembers)) {
      return false;
    }

    if (user.role === "Manager") {
      return true; // Managers can see all files
    } else if (user.role === "Responsible Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member?.member?._id === user.relationship &&
          member.role === "Responsible Lawyer"
      );
    } else if (user.role === "Assistant Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member?.member?._id === user.relationship &&
          member.role === "Treating Lawyer"
      );
    }
    return false;
  }) || []; // Ensure userFiles is always an array

  // Filter disbursements with comprehensive checks
  const filteredDisbursements = disbursements?.filter((disbursement) => {
    const normalizeString = (str) => str?.replace(/\s+/g, "").toLowerCase();

    const matchesSearchTerm = searchTerm
      ? normalizeString(disbursement.type).includes(
          normalizeString(searchTerm)
        ) ||
        normalizeString(disbursement.description).includes(
          normalizeString(searchTerm)
        )
      : true;

    const matchesCaseFilter = caseFilter
      ? disbursement.case?._id === caseFilter._id
      : true;

    const matchesStatusFilter = statusFilter
      ? (statusFilter === "Billed" && disbursement.isBilled) ||
        (statusFilter === "Not billed" && !disbursement.isBilled) ||
        (statusFilter === "Not billable" && disbursement.isNotBillable)
      : true;

    // Check if user has access to the case file
    const hasFileAccess = userFiles.some((file) => file._id === disbursement.case?._id);

    // For managers, show all disbursements regardless of case ownership
    const shouldShowByAccessRules =
      user.role === "Manager" || hasFileAccess;

    if (!shouldShowByAccessRules) {
      return false;
    }

    return (
      matchesSearchTerm &&
      matchesCaseFilter &&
      matchesStatusFilter
    );
  }) || []; // Ensure filteredDisbursements is always an array

  return (
    <Container
      maxWidth="lg"
      sx={{
        backgroundColor: "white",
        boxShadow: "0px 4px 16px rgba(0,0,0,0.013)",
        pt: 2,
        marginBottom: 8,
        marginTop: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          mt: 2,
        }}
      >
        <BreadcrumbNav title={t('pages:disbursements.title')} breadcrumbs={breadcrumbs} />
        <Button
          startIcon={<AddIcon />}
          onClick={() => navigate("/disbursements/create")}
          size="small"
          sx={actionButtonStyles()}
        >
          {t('pages:disbursements.create_new')}
        </Button>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          marginBottom: 2,
          borderRadius: "8px",
        }}
      >
        <TextField
          label={t('common:search')}
          value={searchTerm}
          size="small"
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            sx: {
              padding: "0 10px",
              boxShadow: "none",
            },
          }}
          sx={textFieldStyles({ width: "33.33%", marginRight: "0" })}
        />

        <FileAutocomplete
          options={userFiles}
          value={caseFilter}
          onChange={(event, newValue) => {
            setCaseFilter(newValue);
            setShouldFetchFresh(true);
          }}
          label={t('common:case')}
          name="case"
          size="small"
          icon={<FilterListIcon />}
          width={"33.33%"}
        />
        <Autocomplete
          options={[
            { value: "billed", label: translateStatusFilter("billed") },
            { value: "not_billed", label: translateStatusFilter("not_billed") },
            { value: "not_billable", label: translateStatusFilter("not_billable") }
          ]}
          value={statusFilter ? { 
            value: statusFilter, 
            label: translateStatusFilter(statusFilter)
          } : null}
          onChange={(event, newValue) => {
            setStatusFilter(newValue?.value || null);
            setShouldFetchFresh(true);
          }}
          sx={{ width: "33.34%" }}
          size="small"
          getOptionLabel={(option) => option.label || ""}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('pages:disbursements.filter_by_status')}
              sx={textFieldStyles({ width: "100%" })}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <FilterListIcon />
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}
        />
      </Box>
      <Box>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: "32px 24px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            {delayedCheck && filteredDisbursements?.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "32px 24px",
                }}
              >
                {t('common:no_data_available')}
              </Box>
            ) : (
              <Table sx={tableStyles.container} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableStyles.headerCell}>#</TableCell>
                    <TableCell sx={tableStyles.headerCell}>
                      <TableSortLabel
                        active={orderBy === "type"}
                        direction={orderBy === "type" ? order : "asc"}
                        onClick={() => handleRequestSort("type")}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>

                    <TableCell sx={tableStyles.headerCell}>
                      <TableSortLabel
                        active={orderBy === "case"}
                        direction={orderBy === "case" ? order : "asc"}
                        onClick={() => handleRequestSort("case")}
                      >
                        Case
                      </TableSortLabel>
                    </TableCell>

                    <TableCell sx={tableStyles.headerCell}>
                      <TableSortLabel
                        active={orderBy === "isBilled"}
                        direction={orderBy === "isBilled" ? order : "asc"}
                        onClick={() => handleRequestSort("isBilled")}
                      >
                        {t('common:status')}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell sx={tableStyles.headerCell}>
                      <TableSortLabel
                        active={orderBy === "date"}
                        direction={orderBy === "date" ? order : "asc"}
                        onClick={() => handleRequestSort("date")}
                      >
                        {t('common:date')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={tableStyles.headerCell}>
                      {t('common:description')}
                    </TableCell>
                    <TableCell sx={tableStyles.headerCell}>
                      <TableSortLabel
                        active={orderBy === "amount"}
                        direction={orderBy === "amount" ? order : "asc"}
                        onClick={() => handleRequestSort("amount")}
                      >
                        {t('common:amount')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={tableStyles.headerCell}>
                      {t('common:actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDisbursements
                    ?.sort((a, b) => {
                      if (orderBy) {
                        let aValue, bValue;

                        // Handle nested properties for case
                        if (orderBy === "case") {
                          aValue = a.case?.name;
                          bValue = b.case?.name;
                        } else if (orderBy === "date") {
                          aValue = new Date(a.date).getTime();
                          bValue = new Date(b.date).getTime();
                        } else {
                          aValue = a[orderBy];
                          bValue = b[orderBy];
                        }

                        // Null check and comparison
                        if (aValue === null || aValue === undefined)
                          return order === "asc" ? -1 : 1;
                        if (bValue === null || bValue === undefined)
                          return order === "asc" ? 1 : -1;

                        if (
                          typeof aValue === "string" &&
                          typeof bValue === "string"
                        ) {
                          return order === "asc"
                            ? aValue.localeCompare(bValue)
                            : bValue.localeCompare(aValue);
                        }

                        return order === "asc"
                          ? aValue < bValue
                            ? -1
                            : aValue > bValue
                            ? 1
                            : 0
                          : aValue > bValue
                          ? -1
                          : aValue < bValue
                          ? 1
                          : 0;
                      }
                      return 0;
                    })
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((disbursement, index) => (
                      <TableRow key={disbursement._id} sx={tableStyles.row}>
                        <TableCell sx={tableStyles.cell}>
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tableStyles.cell,
                            "&:hover": {
                              textDecoration: "underline",
                              cursor: "pointer",
                            },
                            color: "#005b92",
                            lineHeight: "12px",
                          }}
                          onClick={() =>
                            navigate(`/disbursements/edit/${disbursement._id}`)
                          } // Navigate to the specific disbursement editing page
                        >
                          {disbursement.type}
                        </TableCell>

                        <TableCell
                          sx={{
                            ...tableStyles.cell,
                            "&:hover": {
                              cursor: "pointer",
                              textDecoration: "underline",
                            },
                            color: "#005b92",
                          }}
                          onClick={() =>
                            navigate(`/files/${disbursement.case._id}/overview`)
                          }
                        >
                          {disbursement.case ? (
                            <>
                              <Stack direction="row">
                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: "0.7rem" }} // Smaller font size
                                >
                                  {disbursement.case.fileNo}{" "}
                                  {truncateText(disbursement.case.name, 30)}
                                </Typography>
                              </Stack>
                            </>
                          ) : (
                            "No case"
                          )}
                        </TableCell>
                        <TableCell sx={tableStyles.cell}>
                          <span
                            style={{
                              padding: "1px 6px",
                              borderRadius: "20px",
                              backgroundColor: disbursement.isBilled
                                ? "#85bc20"
                                : "#e5202e", // Green for Billed, Red for Not Billed
                              color: disbursement.isBilled ? "black" : "white",
                              fontSize: "8.25px",
                              fontFamily:
                                '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                              fontWeight: "700",
                              textTransform: "upperCase",
                              height: "12px",
                              letterSpacing: "0.2em",
                            }}
                          >
                            {disbursement.isBilled ? t('pages:disbursements.billed') : t('pages:disbursements.not_billed')}
                          </span>
                        </TableCell>
                        <TableCell sx={tableStyles.cell}>
                          {new Date(disbursement.date).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </TableCell>
                        <TableCell sx={tableStyles.cell}>
                          {disbursement.description}
                        </TableCell>
                        <TableCell sx={tableStyles.cell}>
                          € {disbursement.amount.toFixed(2).replace(".", ",")}
                        </TableCell>
                        <TableCell sx={tableStyles.cell}>
                          <Tooltip title={t('pages:disbursements.delete_disbursement')}>
                            <IconButton
                              onClick={() => handleDeleteClick(disbursement)}
                              color="error"
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}
            <TablePagination
              component="div"
              count={filteredDisbursements?.length || 0}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              labelRowsPerPage={t('common:rows_per_page')}
              sx={{ borderTop: "1px solid #ddd", padding: "10px" }}
            />
          </TableContainer>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('pages:disbursements.delete_disbursement')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('pages:disbursements.delete_confirmation')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {t('buttons:cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            autoFocus
          >
            {t('common:delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DisbursementsPage;
